// =============================================================================
// DOMAIN REGISTRY
// =============================================================================
// Single source of truth for meta ring navigation domains.
// Adding a domain = add one entry here. Everything else derives from this.

export type DomainId = "projects" | "contact" | "manifesto" | "resume" | "blog";

export interface DomainHero {
  // Subtitle lines shown under the "Vincent Ngu" lockup when this domain is active.
  // First line renders emphasized (bold + magnetic hover). Omit or leave empty for name-only.
  subtitles?: string[];
}

export interface DomainConfig {
  id: DomainId;
  label: string;
  color: string;
  orbType: "custom" | "generic"; // custom = has its own R3F component (ContactRing, ProjectsMetaOrb)
  hero?: DomainHero;             // Hero copy shown when this domain is active. Omit for name-only.
}

export const DOMAINS: DomainConfig[] = [
  // Order: Works (projects) centered at index 2, matching physical ring model
  {
    id: "resume",
    label: "Resume",
    color: "#5B8DEF",
    orbType: "generic",
    hero: {
      subtitles: [
        "Multi-Surface Architect. Systems Operator.",
        "Web • Mobile • Living Room • Spatial",
      ],
    },
  },
  {
    id: "manifesto",
    label: "About",
    color: "#D4AF37",
    orbType: "generic",
    hero: {
      subtitles: [
        "Multi-Surface Architect. Systems Operator.",
        "Web • Mobile • Living Room • Spatial",
      ],
    },
  },
  {
    id: "projects",
    label: "Work",
    color: "#FF6B35",
    orbType: "custom",
    hero: {
      subtitles: [
        "Multi-Surface Architect. Systems Operator.",
        "Web • Mobile • Living Room • Spatial",
        "Design Lead, Video Player @ Crunchyroll (Sony)",
      ],
    },
  },
  {
    id: "contact",
    label: "Contact",
    color: "#20B2AA",
    orbType: "custom",
    hero: {
      subtitles: [
        "Multi-Surface Architect. Systems Operator.",
        "Web • Mobile • Living Room • Spatial",
        "Available for Consulting, Advising, & Coaching",
      ],
    },
  },
  {
    id: "blog",
    label: "Blog",
    color: "#A78BFA",
    orbType: "generic",
    hero: {
      subtitles: [
        "Multi-Surface Architect. Systems Operator.",
        "Web • Mobile • Living Room • Spatial",
      ],
    },
  },
];

// Derived constants -- computed from DOMAINS, never hardcoded
export const DOMAIN_ORDER: DomainId[] = DOMAINS.map(d => d.id);
export const DOMAIN_COUNT = DOMAINS.length;
export const DOMAIN_SPACING = (Math.PI * 2) / DOMAIN_COUNT;
export const DOMAIN_LABELS = Object.fromEntries(
  DOMAINS.map(d => [d.id, d.label])
) as Record<DomainId, string>;
export const DOMAIN_COLORS = Object.fromEntries(
  DOMAINS.map(d => [d.id, d.color])
) as Record<DomainId, string>;

// Get angle for a domain (where it sits on the ring when ring angle is 0)
export function getDomainBaseAngle(domain: DomainId): number {
  const index = DOMAIN_ORDER.indexOf(domain);
  return index * DOMAIN_SPACING;
}

// Get which domain is at front for a given ring angle
export function getDomainAtFront(ringAngle: number): DomainId {
  const normalized = (((-ringAngle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2));
  const index = Math.round(normalized / DOMAIN_SPACING) % DOMAIN_COUNT;
  return DOMAIN_ORDER[index];
}

// Get adjacent domains (for corner nav labels)
export function getAdjacentDomains(active: DomainId): { left: DomainId; right: DomainId } {
  const idx = DOMAIN_ORDER.indexOf(active);
  const leftIdx = (idx - 1 + DOMAIN_COUNT) % DOMAIN_COUNT;
  const rightIdx = (idx + 1) % DOMAIN_COUNT;
  return {
    left: DOMAIN_ORDER[leftIdx],
    right: DOMAIN_ORDER[rightIdx],
  };
}

// Look up full config for a domain
export function getDomainConfig(id: DomainId): DomainConfig {
  return DOMAINS.find(d => d.id === id)!;
}
